import React, { ForwardedRef, RefObject } from 'react';
import { Box, Grid } from '@mui/material';
import { InvisibleButton } from 'spw-shared/globalComponents';
import { colors } from 'spw-theme';
import makeStyles from '@mui/styles/makeStyles';
import {
  ProgramSummaryDialogTitle,
  ProgramSummaryDialogContent,
  ProgramSummaryDialogBox,
  ProgramSummaryCustomDialog,
} from 'spw-components/dialogs/total-program/styles';
import {
  PaperComponent,
  SEAT_COUNT_USF_VALUES,
} from 'spw-components/dialogs/total-program/utils';
import { ProgramSummaryDialogTable } from 'spw-components/dialogs/total-program/program-summary-dialog-table';
import { ProgramSummaryDialogProps } from 'spw-components/dialogs/total-program/types';
import { applyOverflowingRulesToDefaultPosition } from 'spw-shared/utils';
import useUnitConversion from 'spw-components/unit-conversion/hooks';

const useStyles = makeStyles({
  closeButton: {
    color: colors.mainBlue,
  },
});

interface SeatCountUsfDialogDialogProps extends ProgramSummaryDialogProps {
  anchorRef: ForwardedRef<HTMLElement>;
  showTotalUsfFutureWorkSeats: () => void;
  showProgramUsfFutureWorkSeats: () => void;
}

const VERTICAL_DIFF = 180;

const getTopLeft = (element: HTMLElement) => {
  return applyOverflowingRulesToDefaultPosition(
    (rect) => {
      const right = window.innerWidth - rect.left - rect.width;

      return {
        x: right + rect.width,
        y: rect.top - VERTICAL_DIFF,
      };
    },
    element,
    undefined,
    'right'
  );
};

const SeatCountUsfDialog: React.FC<SeatCountUsfDialogDialogProps> = ({
  anchorRef,
  showDialog,
  setShowDialog,
  totalProgramWorksheets,
  value,
  valueType,
  showTotalUsfFutureWorkSeats,
  showProgramUsfFutureWorkSeats,
}) => {
  const { getUnit } = useUnitConversion();
  const classes = useStyles({});

  return (
    <ProgramSummaryCustomDialog
      open={showDialog}
      disablePortal
      disableEnforceFocus
      disableScrollLock
      hideBackdrop
      PaperProps={{
        handle: '#draggable-seat-count-usf',
        component: PaperComponent,
      }}
      anchorRef={anchorRef as RefObject<HTMLElement>}
      getTopLeft={getTopLeft}
    >
      <ProgramSummaryDialogTitle id="draggable-seat-count-usf">
        <Box display="flex">
          <Box alignItems="center" display="flex">
            Seat Count {getUnit('area')}
          </Box>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            flexGrow="1"
          >
            <InvisibleButton
              className={classes.closeButton}
              onClick={() => {
                setShowDialog(false);
              }}
            >
              CLOSE
            </InvisibleButton>
          </Box>
        </Box>
      </ProgramSummaryDialogTitle>

      <ProgramSummaryDialogContent>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <ProgramSummaryDialogBox
              className={
                valueType === SEAT_COUNT_USF_VALUES.TOTAL_USF_FUTURE_WORK_SEATS
                  ? 'selected'
                  : 'selectable'
              }
              onClick={showTotalUsfFutureWorkSeats}
            >
              Total Available {getUnit('area')} /
              <br />
              Work Seats
            </ProgramSummaryDialogBox>
          </Grid>

          <Grid item xs={6}>
            <ProgramSummaryDialogBox
              className={
                valueType ===
                SEAT_COUNT_USF_VALUES.PROGRAM_USF_FUTURE_WORK_SEATS
                  ? 'selected'
                  : 'selectable'
              }
              onClick={showProgramUsfFutureWorkSeats}
            >
              Total Program {getUnit('area')} /
              <br />
              Work Seats
            </ProgramSummaryDialogBox>
          </Grid>
        </Grid>
        <ProgramSummaryDialogTable
          value={value}
          valueType={valueType}
          totalProgramWorksheets={totalProgramWorksheets}
        />
      </ProgramSummaryDialogContent>
    </ProgramSummaryCustomDialog>
  );
};

export default SeatCountUsfDialog;
